import { Arrow, Content, Portal, Provider, Root, Trigger } from '@radix-ui/react-tooltip'
import type { ReactElement, ReactNode } from 'react'
import cc from 'classcat'

import type { PopoverAlign, PopoverSide } from '../Popover/types'

const DEFAULT_DELAY_DURATION = 50

type TooltipVariant = 'light' | 'dark'

interface TooltipProps {
  readonly trigger?: ReactNode | string
  readonly content: ReactNode
  readonly side?: PopoverSide
  readonly align?: PopoverAlign
  readonly sideOffset?: number
  readonly open?: boolean
  readonly usePortal?: boolean
  readonly disabled?: boolean
  readonly hideIcon?: boolean
  readonly variant?: TooltipVariant
  readonly compact?: boolean
  readonly hoverableContent?: boolean
}

export function Tooltip({
  trigger,
  content,
  side,
  align,
  sideOffset,
  open,
  usePortal = false,
  disabled,
  hideIcon,
  variant = 'dark',
  compact = false,
  hoverableContent = false,
}: TooltipProps): ReactElement {
  const ContentWrapper = usePortal ? Portal : 'div'

  return (
    <Provider>
      <Root
        disableHoverableContent={!hoverableContent}
        delayDuration={DEFAULT_DELAY_DURATION}
        open={disabled ? false : open}
      >
        <Trigger
          className={cc(['tooltip-trigger', { 'ep-info-bubble': !hideIcon }])}
          data-testid="tooltip-trigger"
          asChild
        >
          <span>{trigger}</span>
        </Trigger>
        <ContentWrapper>
          <Content
            className={cc([`tooltip-content`, `tooltip-content-${variant}`, { 'tooltip-content-compact': compact }])}
            side={side}
            align={align}
            sideOffset={sideOffset}
            collisionPadding={10}
            avoidCollisions={true}
            data-testid="tooltip-content"
          >
            {content}
            {!compact && <Arrow className={cc([`tooltip-arrow`, `tooltip-arrow-${variant}`])} />}
          </Content>
        </ContentWrapper>
      </Root>
    </Provider>
  )
}
